import { Alert } from "antd";
import { graphqlPost } from "api/graphqlClient";
import { showConfirmModal } from "components/Modal/showConfirmModal";
import {
    AttributeImpactCancelled,
    AttributeImpactChange,
    buildAttributeImpactMessage,
} from "config/resources/objectTemplateAttributeImpact";
import { getSearchObj } from "utils/utils";

const CUSTOM_OBJECT_COUNT_QUERY = `query objectTemplateCustomObjectCount($filter: String) {
    custom_object_list(filter: $filter, limit: 0, offset: 0) {
        custom_object_count
    }
}`;

/**
 * Number of (non-deleted) custom objects based on an object template.
 * Resolves null when the count cannot be read.
 */
export const fetchObjectTemplateCustomObjectCount = async (
    objectTemplateId: string
): Promise<number | null> => {
    try {
        const response = (await graphqlPost({
            query: CUSTOM_OBJECT_COUNT_QUERY,
            variables: {
                filter: JSON.stringify(
                    getSearchObj({ field: "object_template_id", op: "eq", value: objectTemplateId })
                ),
            },
            allow_normal: true,
        })) as any;
        const count = response?.data?.custom_object_list?.custom_object_count;
        return typeof count === "number" ? count : null;
    } catch {
        return null;
    }
};

/**
 * Tell the user how many custom objects an attribute change affects, before it
 * is saved. Resolves when the change may go ahead; rejects with
 * AttributeImpactCancelled when the user cancels.
 *
 * Resolves immediately (no dialog) when there are no changes, no template id,
 * the template has no custom objects, or the count cannot be read: the notice
 * informs, it must never block a save on its own.
 */
export const confirmAttributeImpact = async ({
    objectTemplateId,
    changes,
}: {
    objectTemplateId?: string | null;
    changes: AttributeImpactChange[];
}): Promise<void> => {
    if (!objectTemplateId || changes.length === 0) {
        return;
    }
    const count = await fetchObjectTemplateCustomObjectCount(objectTemplateId);
    if (!count) {
        return;
    }
    const message = buildAttributeImpactMessage(changes, count);
    await new Promise<void>((resolve, reject) => {
        showConfirmModal({
            title: message.title,
            content: (
                <div className="attributeImpactNotice">
                    <Alert
                        type={message.isWarning ? "warning" : "info"}
                        showIcon
                        message={message.summary}
                        description={
                            message.details.length === 1 ? (
                                message.details[0]
                            ) : (
                                <ul>
                                    {message.details.map((detail) => (
                                        <li key={detail}>{detail}</li>
                                    ))}
                                </ul>
                            )
                        }
                    />
                </div>
            ),
            confirmText: message.confirmText,
            confirmVariation: message.confirmVariation,
            onConfirm: () => resolve(),
            onCancel: () => reject(new AttributeImpactCancelled()),
            className: "confirmationModal",
            centered: true,
            exclusive: true,
        });
    });
};
